<script setup lang="ts">
import { usePlannerTemplateRepeats } from "@/composables/usePlannerTemplateRepeats";
import type { IMultiSelectItem } from "@/interfaces";
import { usePlannerTemplateForm } from "@/stores/plannerTemplateFormStore";
import RRuleTextDisplayer from "@/stories/displays/RRuleText/RRuleTextDisplayer.vue";
import FormGroup from "@/stories/form/FormGroup/FormGroup.vue";
import OptionSelectorInput from "@/stories/form/OptionSelectorInput/OptionSelectorInput.vue";
import SelectInput from "@/stories/form/SelectInput/SelectInput.vue";
import TimePicker from "@/stories/form/TimePicker/TimePicker.vue";
import { toUnix } from "@/stories/utils/dates";
import type { PrimitiveModel, RRuleState } from "@/types";
import TemplateRepeatRuleDialog from "@/views/planner/dialogs/templates/form/dialogs/TemplateRepeatRuleDialog.vue";
import FormContent from "@/views/planner/dialogs/templates/form/FormContent.vue";
import { computed, ref } from "vue";

const store = usePlannerTemplateForm();
const defaultCadence = ref<number | undefined>(store.form.rruleState?.cadence);
const isRepeatModalOpen = ref(false);

const items: IMultiSelectItem[] = [
  { id: "5", title: "5", description: "min" },
  { id: "15", title: "15", description: "min" },
  { id: "30", title: "30", description: "min" },
  { id: "45", title: "45", description: "min" },
  { id: "60", title: "1", description: "time" },
  { id: "120", title: "2", description: "timer" },
  { id: "180", title: "3", description: "timer" },
  { id: "240", title: "4", description: "timer" },
];

const { possibleRepeatRuleOptions } = usePlannerTemplateRepeats();

const repetitionSelected = (value: PrimitiveModel) => {
  if (!value) {
    store.form.rruleState = undefined;
    defaultCadence.value = undefined;
  } else {
    defaultCadence.value = value as number;
    isRepeatModalOpen.value = true;
  }
};

const handleRepeatModalClose = () => {
  isRepeatModalOpen.value = false;
  defaultCadence.value = store.form.rruleState?.cadence;
};

const handleRepeatRuleUpdate = (value: RRuleState) => {
  store.form.rruleState = value;
  isRepeatModalOpen.value = false;
};

const repeatRuleStateDate = computed(() => {
  const date = new Date();
  const [hours, minutes] = store.form.startTime.split(":");
  date.setHours(Number(hours));
  date.setMinutes(Number(minutes));
  return toUnix(date);
});

const handleTimeChange = (time: string) => {
  store.form.startTime = time;

  if (store.form.rruleState) {
    const date = new Date();
    const [hours, minutes] = store.form.startTime.split(":");
    date.setHours(Number(hours));
    date.setMinutes(Number(minutes));
    store.form.rruleState.startDate = toUnix(date);
  }
};
</script>

<template>
  <FormContent>
    <div class="h600 text-neutral1000 pb-6">
      {{ $t("global.time") }}
    </div>
    <FormGroup>
      <TimePicker
        name="startTime"
        format="24h"
        :label="$t('global.time_start')"
        :validation="store.validation"
        :modelValue="store.form.startTime"
        @update:modelValue="handleTimeChange"
      />

      <OptionSelectorInput
        name="duration"
        :label="$t('templates.duration_of_the_task')"
        :items="items"
        :allowDeselect="false"
        v-model="store.form.duration"
      >
        <template #title="{ item }">
          <div class="flex justify-center items-center gap-1">
            <span class="h500">{{ item.title }}</span>
            <span class="b300 text-neutral700">{{ item.description }}</span>
          </div>
        </template>
      </OptionSelectorInput>

      <SelectInput
        name="repeat"
        :items="possibleRepeatRuleOptions"
        :label="$t('global.recurrence')"
        :autoDeselect="false"
        :modelValue="defaultCadence"
        @update:modelValue="repetitionSelected"
      />

      <RRuleTextDisplayer
        v-if="store.form.rruleState"
        :state="store.form.rruleState"
      />
    </FormGroup>
  </FormContent>

  <TemplateRepeatRuleDialog
    v-if="isRepeatModalOpen"
    storeName="PLANNER_TEMPLATE_FORM"
    :initialValue="{
      ...store.form.rruleState,
      cadence: defaultCadence || 7,
      startDate: repeatRuleStateDate,
    }"
    @close="handleRepeatModalClose"
    @update="handleRepeatRuleUpdate"
  />
</template>
